// src/components/auth/EmailVerificationSuccess.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, MessageCircle, RefreshCw, LogIn } from 'lucide-react';
import { trackSignupConversion } from '../../utils/analytics';

const EmailVerificationSuccess = ({ api, onLoginSuccess }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'expired', 'already_verified'
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const hasCalledRef = useRef(false); // Add this to prevent duplicate calls

  // Updated useEffect with duplicate call prevention
  useEffect(() => {
    console.error('useEffect triggered with token:', token);
    if (token && !hasCalledRef.current) { // Only call if we haven't called it before
      hasCalledRef.current = true; // Mark as called
      console.error('About to call verifyEmail');
      verifyEmail();
    } else {
      console.error('No token found or already called verification');
    }
  }, [token]); // Only depend on token, not status

  const verifyEmail = async () => {
    console.error('verifyEmail function called');
    try {
      setStatus('verifying');
      console.error('About to call api.verifyEmail with token:', token);
      const response = await api.verifyEmail(token);
      console.error('api.verifyEmail returned:', response);
      
      if (response.success) {
        setStatus('success');
        setMessage(response.message);
        setUserData(response);

        // Track Google Analytics conversion for email signup
        trackSignupConversion({
          method: 'email',
          plan: response.subscription?.plan || 'FREE',
          userId: response.user?.id,
          isNewUser: true
        });

        // Auto-login the user
        if (response.token) {
          api.setToken(response.token);

          if (response.workspace) {
            api.setCurrentWorkspace(response.workspace);
          }

          // Redirect to dashboard after a short delay
          setTimeout(() => {
            onLoginSuccess(response.user);
            navigate('/dashboard');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Email verification error:', error);
      
      if (error.message.includes('expired')) {
        setStatus('expired');
        setMessage('Your verification link has expired. Please request a new one.');
      } else if (error.message.includes('already verified')) {
        setStatus('already_verified');
        setMessage('Your email is already verified. You can sign in now.');
      } else {
        setStatus('error');
        setMessage(error.message || 'Failed to verify email. Please try again.');
      }
    }
  };

  const handleResendVerification = () => {
    // Redirect to login page where user can request new verification
    navigate('/login');
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
              <RefreshCw className="w-10 h-10 text-purple-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Verifying your email...
            </h2>
            <p className="text-gray-600">
              Please wait while we verify your account.
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Email verified successfully!
            </h2>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            <div className="bg-green-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-700">
                Redirecting you to your dashboard...
              </p>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-6">
              <AlertCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Verification link expired
            </h2>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            <button
              onClick={handleResendVerification}
              disabled={isResending}
              className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isResending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Redirecting...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Go to Sign In
                </>
              )}
            </button>
          </div>
        );

      case 'already_verified':
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
              <CheckCircle className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Already verified
            </h2>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            <button
              onClick={handleGoToLogin}
              className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign in to your account
            </button>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Verification failed
            </h2>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleGoToLogin}
                className="block w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2 inline" />
                Try signing in
              </button>
              <button
                onClick={handleResendVerification}
                className="block w-full px-6 py-3 text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2 inline" />
                Request new verification email
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl mb-4 shadow-lg">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">TicketDesk</h1>
          <p className="text-gray-600 mt-2">Customer Support Platform</p>
        </div>

        {/* Verification Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationSuccess;